import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEnvelope } from 'react-icons/fa';

const SECTIONS = [
  {
    id: 'eligibility',
    title: '1. Eligibility and Account Registration',
    content: [
      {
        sub: '1.1 Eligibility Criteria',
        bullets: [
          'You must be at least 18 years of age to create an account and use the Platform.',
          'You must be legally capable of entering into a binding contract under the Indian Contract Act, 1872.',
          'Organisations registering as Recruiters must be duly incorporated or registered entities under applicable Indian law.',
          'You must not have been previously banned or suspended from JobxBharat.',
        ],
      },
      {
        sub: '1.2 Account Registration',
        text: 'To access most features of the Platform, you must create a user account. You agree to:',
        bullets: [
          'Provide accurate, complete, and current information during registration and keep it updated.',
          'Maintain the confidentiality of your account credentials and not share them with any third party.',
          'Take full responsibility for all activities conducted through your account.',
          'Notify us immediately at supportus@jobxbharat.com of any unauthorized access or security breach.',
        ],
        footer: 'JobxBharat reserves the right to verify your identity and reject or suspend accounts that provide false or misleading information.',
      },
    ],
  },
  {
    id: 'job-seekers',
    title: '2. Platform Usage — Job Seekers',
    content: [
      {
        sub: '2.1 Permitted Use',
        text: 'As a Job Seeker, you may use the Platform to:',
        bullets: [
          'Create and manage your professional profile and resume.',
          'Search for job listings and apply to eligible positions.',
          'Receive job alerts and recruiter messages.',
          'Track the status of your job applications.',
        ],
      },
      {
        sub: '2.2 Job Seeker Obligations',
        bullets: [
          'All information submitted in your profile, resume, and job applications must be truthful and accurate.',
          'You must not misrepresent your qualifications, experience, or identity at any stage.',
          'You are solely responsible for verifying the authenticity of job listings before applying or sharing personal information with employers outside the platform.',
          'You agree not to use the Platform to solicit business from other users or for any commercial purpose unrelated to job seeking.',
        ],
      },
    ],
    notice: {
      title: 'Important Disclaimer for Job Seekers',
      items: [
        'JobxBharat acts solely as a job listing and matchmaking platform.',
        'We do not guarantee employment, interview calls, or salary outcomes.',
        'Verify all job offers independently before accepting or making payments.',
        'Never pay fees to employers claiming to process your application through our platform.',
      ],
    },
  },
  {
    id: 'recruiters',
    title: '3. Platform Usage — Recruiters and Employers',
    content: [
      {
        sub: '3.1 Recruiter Account Registration',
        text: 'Organisations or individuals registering as Recruiters must provide accurate company details and are responsible for the conduct of all sub-users added to their recruiter account.',
      },
      {
        sub: '3.2 Job Posting Standards',
        text: 'Recruiters agree that all job postings on JobxBharat must:',
        bullets: [
          'Accurately describe the role, responsibilities, required qualifications, and compensation.',
          'Represent genuine, currently open positions within a legitimate organisation.',
          'Comply with all applicable labour laws, including those related to equal opportunity, anti-discrimination, and minimum wages.',
          'Not solicit fees, deposits, or payments of any kind from job seekers.',
          'Not promote multi-level marketing (MLM), pyramid schemes, or fraudulent business opportunities.',
        ],
      },
      {
        sub: '3.3 Recruiter Obligations',
        bullets: [
          'Recruiters must respond to applications in a timely and professional manner.',
          'Recruiters must not misuse candidate data for purposes beyond hiring within the scope of the specific job posting.',
          'Recruiters must comply with all applicable data protection obligations when processing candidate information.',
          'Bulk downloading of candidate profiles for purposes other than active recruitment is strictly prohibited.',
        ],
      },
    ],
  },
  {
    id: 'prohibited-conduct',
    title: '4. Prohibited Conduct',
    text: 'The following actions are strictly prohibited on the JobxBharat platform:',
    bullets: [
      'Post false, misleading, or fraudulent job listings or profiles.',
      'Impersonate another person, company, or entity.',
      'Harass, threaten, or abuse other users through any platform feature.',
      'Upload or share malicious software, viruses, or harmful code.',
      'Scrape, crawl, or extract data from the Platform using automated tools without written consent.',
      'Attempt to reverse-engineer, hack, or disrupt the Platform\'s systems.',
      'Use the Platform for spam, unsolicited communications, or phishing.',
      'Share content that is defamatory, obscene, unlawful, or violates third-party intellectual property rights.',
      'Create multiple accounts or fake profiles to misrepresent identity or circumvent bans.',
      'Use the Platform for activities that violate any applicable laws of India.',
    ],
  },
  {
    id: 'intellectual-property',
    title: '5. Content and Intellectual Property',
    content: [
      {
        sub: '5.1 User-Generated Content',
        text: 'You retain ownership of the content you upload to the Platform (resumes, profiles, job postings). By submitting content, you grant JobxBharat a non-exclusive, worldwide, royalty-free license to display, reproduce, and use your content solely for the purpose of operating and improving the Platform.',
      },
      {
        sub: '5.2 Platform Intellectual Property',
        text: 'All content, design elements, software, logos, trademarks, databases, and brand assets on the Platform are the exclusive intellectual property of JobxBharat or its licensors. Unauthorized reproduction, distribution, or commercial use of any Platform content is strictly prohibited and may result in legal action.',
      },
      {
        sub: '5.3 Feedback and Suggestions',
        text: 'Any feedback, ideas, or suggestions you provide regarding the Platform may be used by JobxBharat without obligation to compensate you, and by submitting feedback, you waive any claims over such contributions.',
      },
    ],
  },
  {
    id: 'payments',
    title: '6. Payments and Subscriptions',
    content: [
      {
        sub: '6.1 Paid Services',
        text: 'Certain features of JobxBharat — including premium job listings, featured employer profiles, and advanced candidate search — may be offered on a paid basis. Pricing and subscription plans will be clearly communicated at the time of purchase.',
      },
      {
        sub: '6.2 Payment Terms',
        bullets: [
          'All payments are to be made through authorized payment gateways supported by the Platform.',
          'Prices are quoted in Indian Rupees (INR) and are inclusive of applicable taxes unless stated otherwise.',
          'Subscription fees are non-refundable unless explicitly stated in the specific plan terms or required by law.',
          'Failure to pay outstanding dues may result in suspension or downgrade of your account.',
        ],
      },
      {
        sub: '6.3 Refund Policy',
        text: 'Refund requests will be reviewed on a case-by-case basis. Refunds may be issued at JobxBharat\'s discretion where services were not delivered as described, or where a technical error attributable to us resulted in a failed transaction. To raise a refund request, contact supportus@jobxbharat.com within 7 days of the transaction.',
      },
    ],
  },
  {
    id: 'liability',
    title: '7. Disclaimers and Limitation of Liability',
    content: [
      {
        sub: '7.1 Platform Disclaimers',
        text: 'JobxBharat provides its services on an "as-is" and "as-available" basis. We do not warrant that:',
        bullets: [
          'The Platform will be uninterrupted, error-free, or free of viruses or harmful components.',
          'Job listings are accurate, complete, or current, as they are submitted by third-party recruiters.',
          'Any particular job seeker will receive interview calls or employment offers.',
          'The results of using the Platform will meet your expectations or requirements.',
        ],
      },
      {
        sub: '7.2 Limitation of Liability',
        text: 'To the maximum extent permitted under applicable Indian law, JobxBharat and its officers, directors, employees, and partners shall not be liable for:',
        bullets: [
          'Indirect, incidental, special, consequential, or punitive damages arising from Platform use.',
          'Loss of employment opportunities, earnings, or career outcomes.',
          'Unauthorized access to or alteration of your transmissions or data.',
          'Conduct of third-party employers, recruiters, or users on or off the Platform.',
          'Any matter beyond our reasonable control, including force majeure events.',
        ],
        footer: 'Our total aggregate liability to you shall not exceed the amount paid by you (if any) to JobxBharat in the three months preceding the claim.',
      },
      {
        sub: '7.3 Third-Party Links and Services',
        text: 'The Platform may contain links to external websites or third-party services. JobxBharat is not responsible for the content, accuracy, or practices of such third parties. Accessing external links is entirely at your own risk.',
      },
    ],
  },
  {
    id: 'termination',
    title: '8. Account Suspension and Termination',
    content: [
      {
        sub: '8.1 Termination by User',
        text: 'You may delete your account at any time through the account settings page. Upon deletion, your active profile will be removed from public view. Certain data may be retained for the period specified in our Privacy Policy.',
      },
      {
        sub: '8.2 Termination by JobxBharat',
        text: 'We reserve the right to suspend, restrict, or permanently terminate your account, with or without notice, if we determine that:',
        bullets: [
          'You have violated any provision of these Terms or our policies.',
          'Your account activity poses a risk of fraud, harm, or legal liability to users or the Platform.',
          'We are required to do so by applicable law or a valid government/court order.',
          'Your account has been inactive for a prolonged period (typically 24 months).',
        ],
        footer: 'In case of termination due to policy violations, any active paid subscription will not be refunded.',
      },
    ],
  },
  {
    id: 'indemnification',
    title: '9. Indemnification',
    text: 'You agree to indemnify, defend, and hold harmless JobxBharat, its affiliates, officers, employees, and service providers from and against any claims, liabilities, losses, damages, costs, and expenses (including reasonable legal fees) arising out of:',
    bullets: [
      'Your use of the Platform or violation of these Terms.',
      'Content you submit, post, or share on the Platform.',
      'Your infringement of any third-party rights, including intellectual property or privacy rights.',
      'Any misrepresentation made by you in connection with your account or interactions on the Platform.',
    ],
  },
  {
    id: 'governing-law',
    title: '10. Governing Law and Dispute Resolution',
    content: [
      {
        sub: '10.1 Governing Law',
        text: 'These Terms shall be governed by and construed in accordance with the laws of India, including but not limited to the Information Technology Act, 2000, the Indian Contract Act, 1872, and other applicable statutes.',
      },
      {
        sub: '10.2 Dispute Resolution',
        text: 'In the event of a dispute arising out of or relating to these Terms or use of the Platform, the parties agree to the following dispute resolution process:',
        items: [
          { label: 'Informal Resolution', text: 'Raise the dispute with our support team at supportus@jobxbharat.com. We will make reasonable efforts to resolve it within 30 days.' },
          { label: 'Mediation', text: 'If informal resolution fails, parties may opt for mediation through a mutually agreed mediator.' },
          { label: 'Arbitration', text: 'Unresolved disputes shall be referred to binding arbitration under the Arbitration and Conciliation Act, 1996. The seat of arbitration shall be India.' },
          { label: 'Jurisdiction', text: 'For any disputes not covered by arbitration, the courts of India shall have exclusive jurisdiction.' },
        ],
      },
    ],
  },
  {
    id: 'modifications',
    title: '11. Modifications to Terms',
    text: 'JobxBharat reserves the right to amend, update, or replace these Terms at any time. We will notify users of material changes through:',
    bullets: [
      'A prominent banner or notice on the Platform homepage.',
      'An email notification to your registered email address.',
      'In-app alerts or push notifications.',
    ],
    footer: 'The updated Terms will be effective upon posting unless otherwise stated. Your continued use of the Platform after the effective date constitutes your acceptance of the revised Terms.',
  },
  {
    id: 'miscellaneous',
    title: '12. Miscellaneous Provisions',
    items: [
      { label: 'Entire Agreement', text: 'These Terms constitute the entire agreement between you and JobxBharat with respect to the Platform, superseding all prior agreements.' },
      { label: 'Severability', text: 'If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force.' },
      { label: 'Waiver', text: 'Our failure to enforce any provision of these Terms shall not constitute a waiver of such provision.' },
      { label: 'Assignment', text: 'You may not assign your rights or obligations under these Terms without our prior written consent.' },
      { label: 'Notices', text: 'Communications from JobxBharat will be sent to your registered email address. Please keep it updated.' },
    ],
  },
  {
    id: 'contact',
    title: '13. Contact Us',
    contact: {
      name: 'JobxBharat Legal & Support Team',
      email: 'supportus@jobxbharat.com',
      address: 'India',
      hours: 'Monday to Friday, 10:00 AM – 6:00 PM IST',
      response: '30 days',
      extra: [
        { label: 'General Support', email: 'supportus@jobxbharat.com' },
        { label: 'Privacy Queries', email: 'supportus@jobxbharat.com' },
        { label: 'Billing Queries', email: 'supportus@jobxbharat.com' },
      ],
    },
  },
];

function BulletList({ items }) {
  return (
    <ul className="space-y-2 mt-3">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3 text-sm text-ink-500 leading-relaxed">
          <span className="w-1.5 h-1.5 rounded-full bg-brand-purple mt-2 shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  );
}

function LabeledList({ items }) {
  return (
    <ul className="space-y-3 mt-3">
      {items.map((item, i) => (
        <li key={i} className="text-sm leading-relaxed">
          <span className="font-bold text-ink-900">{item.label}: </span>
          <span className="text-ink-500">{item.text}</span>
        </li>
      ))}
    </ul>
  );
}

function NoticeBox({ notice }) {
  return (
    <div className="mt-5 p-5 rounded-2xl bg-amber-50 border border-amber-200">
      <p className="font-display font-black text-sm text-amber-800 mb-3">{notice.title}</p>
      <ul className="space-y-2">
        {notice.items.map((item, i) => (
          <li key={i} className="flex gap-3 text-sm text-amber-700 leading-relaxed">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Section({ section }) {
  return (
    <div id={section.id} className="scroll-mt-28 pb-10 border-b border-gray-100 last:border-0">
      <h2 className="font-display font-black text-xl md:text-2xl text-ink-900 mb-4">
        {section.title}
      </h2>

      {section.text && <p className="text-sm text-ink-500 leading-relaxed">{section.text}</p>}
      {section.bullets && <BulletList items={section.bullets} />}
      {section.items && <LabeledList items={section.items} />}

      {section.content && section.content.map((block, i) => (
        <div key={i} className="mt-5">
          {block.sub && (
            <h3 className="font-display font-bold text-base text-ink-900 mb-2">{block.sub}</h3>
          )}
          {block.text && <p className="text-sm text-ink-500 leading-relaxed">{block.text}</p>}
          {block.items && <LabeledList items={block.items} />}
          {block.bullets && <BulletList items={block.bullets} />}
          {block.footer && (
            <p className="mt-3 text-sm text-ink-500 leading-relaxed italic">{block.footer}</p>
          )}
        </div>
      ))}

      {section.notice && <NoticeBox notice={section.notice} />}

      {section.footer && (
        <p className="mt-4 text-sm text-ink-500 leading-relaxed italic">{section.footer}</p>
      )}

      {section.contact && (
        <div className="mt-4 p-5 rounded-2xl bg-secondary-50 border border-secondary-100 space-y-2 text-sm">
          <p><span className="font-bold text-ink-900">Team:</span> <span className="text-ink-500">{section.contact.name}</span></p>
          <p>
            <span className="font-bold text-ink-900">Legal / Compliance:</span>{' '}
            <a href={`mailto:${section.contact.email}`} className="text-brand-purple hover:underline">{section.contact.email}</a>
          </p>
          {section.contact.extra && section.contact.extra.map((e, i) => (
            <p key={i}>
              <span className="font-bold text-ink-900">{e.label}:</span>{' '}
              <a href={`mailto:${e.email}`} className="text-brand-purple hover:underline">{e.email}</a>
            </p>
          ))}
          <p><span className="font-bold text-ink-900">Address:</span> <span className="text-ink-500">{section.contact.address}</span></p>
          <p><span className="font-bold text-ink-900">Response Time:</span> <span className="text-ink-500">{section.contact.response}</span></p>
          <p><span className="font-bold text-ink-900">Hours:</span> <span className="text-ink-500">{section.contact.hours}</span></p>
        </div>
      )}
    </div>
  );
}

export default function TermsAndConditions() {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-paper text-ink-900">

      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm font-semibold text-ink-500
                       hover:text-ink-900 transition-colors"
          >
            <FaArrowLeft /> Back to Home
          </button>
          <span className="font-brand font-black text-lg text-ink-900">Jobx<span className="text-brand-saffron">Bharat</span></span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-16 lg:grid lg:grid-cols-[260px_1fr] lg:gap-16">

        {/* Sidebar TOC — sticky on desktop */}
        <aside className="hidden lg:block">
          <div className="sticky top-28 space-y-1">
            <p className="text-xs font-black uppercase tracking-widest text-ink-300 mb-4">Contents</p>
            {SECTIONS.map(s => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="block text-xs font-semibold text-ink-500 hover:text-brand-purple
                           py-1 border-l-2 border-transparent hover:border-brand-purple
                           pl-3 transition-all leading-snug"
              >
                {s.title}
              </a>
            ))}
          </div>
        </aside>

        {/* Main content */}
        <main>
          {/* Hero header */}
          <div className="mb-12 pb-10 border-b border-gray-100">
            <span className="text-xs font-black tracking-widest uppercase text-brand-purple mb-3 block">
              Legal
            </span>
            <h1 className="font-display font-black text-4xl md:text-5xl text-ink-900 mb-4">
              Terms & Conditions
            </h1>
            <p className="text-ink-500 text-sm">
              Effective Date: <strong>January 1, 2026</strong> &nbsp;|&nbsp; Version 1.0
            </p>
            <p className="text-ink-500 text-sm mt-3 leading-relaxed max-w-2xl">
              These Terms and Conditions constitute a legally binding contract between you and JobxBharat.
              By registering, accessing, or using JobxBharat in any manner, you confirm that you have read,
              understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree,
              please discontinue use of the Platform immediately.
            </p>
          </div>

          <div className="space-y-10">
            {SECTIONS.map(s => <Section key={s.id} section={s} />)}
          </div>

          {/* Contact footer strip */}
          <div className="mt-14 p-6 rounded-2xl bg-brand-purple text-white flex flex-col sm:flex-row
                          items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-display font-black text-lg">Questions about our terms?</p>
              <p className="text-white/70 text-sm mt-1">Our legal team is here to help.</p>
            </div>
            <a
              href="mailto:supportus@jobxbharat.com"
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-brand-purple
                         font-black text-sm hover:brightness-95 transition-all shrink-0"
            >
              <FaEnvelope /> supportus@jobxbharat.com
            </a>
          </div>
        </main>
      </div>

      <footer className="py-8 border-t border-gray-100 text-center text-xs text-ink-300 bg-white">
        © {new Date().getFullYear()} Jobx<span className="text-brand-saffron">Bharat</span> · All rights reserved ·{' '}
        <a href="/privacy-policy" className="hover:text-brand-purple">Privacy Policy</a>
      </footer>
    </div>
  );
}
